import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  Calendar,
  Clock,
  MapPin,
  User,
  ArrowRight,
  Star,
} from 'lucide-react';
import { paymentApi } from '../../services/paymentService';
import {
  formatDisplayMoney,
  paymentCatalogAmount,
} from '../../utils/formatMoney';

const money = (n) => formatDisplayMoney(n, 'USD', 'en-US');

const ClientBookingSuccess = () => {
  const location = useLocation();
  const bookingData = location.state?.bookingData || {};
  const serviceData = location.state?.serviceData || {};
  const vendorData = location.state?.vendorData || {};

  const { data: paymentsResponse } = useQuery({
    queryKey: ['payments', bookingData.id],
    queryFn: () => paymentApi.getPaymentByBooking(bookingData.id),
    enabled: !!bookingData.id,
    select: (response) => response?.data || [],
  });

  const payments = paymentsResponse || [];
  const completedPayments = payments.filter((p) => p.status === 'completed');

  const depositPayment = completedPayments.find((p) => p.payment_type === 'deposit');
  const depositAmount = depositPayment ? paymentCatalogAmount(depositPayment) : 0;

  const fullPayment = completedPayments.find((p) => p.payment_type === 'full_payment');
  const hasFullPayment = !!fullPayment;
  const hasDepositOnly = depositAmount > 0 && !hasFullPayment;
  const paidNow = hasFullPayment
    ? paymentCatalogAmount(fullPayment)
    : depositAmount;

  const packagePrice = Number(bookingData.selectedPackage?.price || 0);
  const totalAmount = Number(
    bookingData.totalAmount ||
      Number(bookingData.subtotal_amount || 0) + Number(bookingData.platform_fee || 0) ||
      0
  );

  const eventDateLabel = bookingData.eventDate
    ? new Date(bookingData.eventDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Not specified';

  if (!bookingData.id) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-m-canvas-light px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-m-border-light bg-m-surface">
            <Check className="h-5 w-5 text-m-ink-faint" strokeWidth={2.5} />
          </div>
          <h1 className="mb-3 text-2xl font-medium tracking-tight text-m-ink">
            No booking found
          </h1>
          <p className="marketing-body mb-8 text-m-ink-muted">
            It looks like you don’t have a valid booking to view.
          </p>
          <Link to="/" className="marketing-btn marketing-btn-ink">
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const reference =
    bookingData.booking_reference ||
    bookingData.bookingReference ||
    String(bookingData.id).slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-m-canvas-light">
      <div className="page-container py-8 sm:py-12">
        <div className="mb-8 text-center sm:mb-10">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-m-ink text-white">
            <Check className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-m-ink-faint">
            You’re all set
          </p>
          <h1 className="marketing-display-md mb-3 text-m-ink">
            Booking confirmed
          </h1>
          <p className="marketing-body mx-auto max-w-lg text-m-ink-muted">
            A confirmation email is on the way. Your vendor will follow up within 24 hours.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-m-border-light bg-m-surface px-3 py-1.5 text-sm text-m-ink-muted">
            Ref <span className="font-medium tracking-wide text-m-ink">{reference}</span>
          </div>
        </div>

        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 lg:grid-cols-5 lg:gap-6">
          <div className="space-y-5 lg:col-span-3">
            <section className="rounded-2xl border border-m-border-light bg-m-surface p-5 sm:p-6 shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
              <h2 className="mb-4 text-sm font-medium text-m-ink">Event details</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail icon={Calendar} label="Date" value={eventDateLabel} />
                <Detail
                  icon={Clock}
                  label="Time"
                  value={bookingData.eventTime || 'Not specified'}
                />
                <Detail
                  icon={MapPin}
                  label="Location"
                  value={bookingData.location || 'Not specified'}
                  className="sm:col-span-2"
                />
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-m-border-light bg-m-surface shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
              <div className="flex items-start justify-between gap-4 border-b border-m-border-light px-5 py-4 sm:px-6">
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wider text-m-ink-faint">
                    Package
                  </p>
                  <h3 className="text-base font-medium text-m-ink">
                    {bookingData.selectedPackage?.name || 'Service package'}
                  </h3>
                  {bookingData.selectedPackage?.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-m-ink-muted">
                      {bookingData.selectedPackage.description}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-lg font-medium tabular-nums tracking-tight text-m-ink">
                    {money(packagePrice)}
                  </p>
                  <p className="mt-0.5 text-xs text-m-ink-faint">
                    {bookingData.duration ? `${bookingData.duration} hours` : 'Duration TBD'}
                  </p>
                </div>
              </div>

              <div className="space-y-2 px-5 py-4 sm:px-6">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-m-ink-muted">Order total</span>
                  <span className="text-sm font-medium tabular-nums text-m-ink">
                    {money(totalAmount)}
                  </span>
                </div>
                {(hasFullPayment || hasDepositOnly) && (
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-m-ink-muted">
                      {hasFullPayment ? 'Paid in full' : 'Deposit paid'}
                    </span>
                    <span className="text-sm font-medium tabular-nums text-m-ink">
                      {money(paidNow)}
                    </span>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-3 rounded-full bg-m-ink px-4 py-3">
                  <span className="text-sm font-medium text-white/80">
                    {hasFullPayment ? 'Amount paid' : 'Paid today'}
                  </span>
                  <span className="text-lg font-medium tabular-nums text-white">
                    {money(paidNow || totalAmount)}
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-m-border-light bg-m-surface p-5 sm:p-6 shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
              <h2 className="mb-4 text-sm font-medium text-m-ink">What’s next</h2>
              <ol className="space-y-4">
                {[
                  {
                    title: 'Check your inbox',
                    body: 'Confirmation with booking details lands shortly.',
                  },
                  {
                    title: 'Vendor reaches out',
                    body: 'Expect contact within 24 hours to lock in the plan.',
                  },
                  {
                    title: 'Enjoy the event',
                    body: 'Show up ready — we’ll handle the rest with your vendor.',
                  },
                ].map((step, i) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-m-canvas-light text-xs font-medium text-m-ink">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-m-ink">{step.title}</p>
                      <p className="mt-0.5 text-sm text-m-ink-muted">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          <div className="space-y-5 lg:col-span-2">
            <section className="rounded-2xl border border-m-border-light bg-m-surface p-5 shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-m-ink-faint">
                Your vendor
              </p>
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-m-ink text-white">
                  <User className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-medium text-m-ink">
                    {vendorData.business_name || 'Vendor'}
                  </h3>
                  <div className="mt-0.5 flex items-center text-xs text-m-ink-muted">
                    <Star className="mr-1 h-3 w-3 fill-current text-m-ink" />
                    {Number(serviceData.average_rating || 0).toFixed(1)}
                    <span className="ml-1 text-m-ink-faint">
                      ({serviceData.total_reviews || 0})
                    </span>
                  </div>
                </div>
              </div>
              {(vendorData.city || vendorData.state) && (
                <div className="flex items-center gap-1.5 text-sm text-m-ink-muted">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {[vendorData.city, vendorData.state].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-m-border-light bg-m-surface p-5 shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-m-ink-faint">
                Service
              </p>
              <dl className="space-y-2 text-sm">
                <Row label="Name" value={serviceData.service_name || '—'} />
                <Row label="Category" value={serviceData.category_name || '—'} />
                <Row
                  label="Duration"
                  value={bookingData.duration ? `${bookingData.duration} hours` : '—'}
                />
                <Row label="Guests" value={bookingData.guestCount || 'Not specified'} />
              </dl>
            </section>

            <div className="flex flex-col gap-2.5 pt-1">
              <Link
                to="/bookings"
                className="auth-btn-outline w-full gap-1.5"
              >
                <Calendar className="h-4 w-4" />
                View my bookings
              </Link>
              <Link
                to="/browse"
                className="marketing-btn marketing-btn-ink w-full gap-1.5"
              >
                Browse more services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Detail({ icon: Icon, label, value, className = '' }) {
  return (
    <div className={`flex gap-3 ${className}`}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-m-canvas-light text-m-ink">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-m-ink-faint">
          {label}
        </p>
        <p className="text-sm leading-snug text-m-ink">{value}</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-m-ink-muted">{label}</dt>
      <dd className="truncate text-right font-medium text-m-ink">{value}</dd>
    </div>
  );
}

export default ClientBookingSuccess;
